import { FC, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { twJoin, twMerge } from 'tailwind-merge';

import { Dag, QueryStatus, ToolbarItem } from '@dbt-labs/dbt-dag';
import {
  IconButton,
  Link,
  RyeconExpand,
  RyeconInfoOutline,
  Sizes,
} from '@dbt-labs/sourdough';

import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { ColumnLineageNode, useColumnLineage } from '@/hooks/discovery/columnLineage';
import { useAllPublicModels } from '@/hooks/discovery/publicModels';
import {
  useConsumerProjects,
  useProducerProjects,
} from '@/hooks/discovery/relatedProjects';
import { useLinkToExplorer } from '@/hooks/links';
import { useUrlProjectParams } from '@/hooks/params';
import { useTargetEnvironmentType } from '@/hooks/useTargetEnvironmentType';
import { ColumnErrorPanelContents } from '@/pages/account/project/columnLineage/components/ColumnErrorPanelContents';
import { ColumnPanelContents } from '@/pages/account/project/columnLineage/components/ColumnPanelContents';
import { parseUniqueId } from '@/typings/parseUniqueId';
import { truthy } from '@/util/array';
import { ExplorerDag, ExplorerDagNode } from '../ExplorerDag';
import { useCurrentPage } from '../Page';
import { ProjectPanelContents } from '../ProjectPanel/ProjectPanelContents';
import { LineageFloatingNavBar } from './LineageFloatingNavBar';
import { LineagePanelView } from './LineagePanelView';

type ColumnLineageViewProps = {
  uniqueId?: string | null;
  columnName?: string | null;
  isFullScreen?: boolean;
  onOpenFullScreenLineage?: () => void;
};

type ColumnLineageViewData = {
  nodeUniqueId: string;
  error?: string | null;
  errorCategory?: string | null;
  columnName: string;
};

type CLLNode = ExplorerDagNode & {
  data?: ColumnLineageViewData;
};

export const getHumanReadableErrorType = (errorCategory: string | null) => {
  switch (errorCategory) {
    case 'ParseError':
      return 'SQL parsing error';
    case 'PythonModelUnsupportedError':
      return 'Python unsupported';
    case 'Duplicate Relation Error':
      return 'Duplicate relations found';
    default:
      return 'Issue found';
  }
};

export const ColumnLineageView: FC<ColumnLineageViewProps> = ({
  uniqueId,
  columnName,
  isFullScreen,
  onOpenFullScreenLineage,
}) => {
  const getLineageLink = useLinkToExplorer('columnLineage');
  const getProjectLink = useLinkToExplorer('project');
  const navigate = useNavigate();
  const getDetailsUrl = useLinkToExplorer('resourceDetails');
  const getTargetEnvironmentType = useTargetEnvironmentType();
  const {
    projectName,
    accountId,
    projectId: currentProjectId,
    environmentId,
    uniqueId: uniqueIdFromContext,
    accountName,
  } = useDbtCloudProject();
  const { getProjectName, hasAccountFeature } = useDbtCloudAccount();
  const projectScope = useUrlProjectParams();
  const [searchParams, setSearchParams] = useSearchParams();

  if (!accountId) throw new Error('Must be in a URL with an account ID');

  const projectIdFromParam = Number(searchParams.get('projectId'));
  const environmentIdFromParam = Number(searchParams.get('environmentId'));
  const uniqueIdFromParam = searchParams.get('uniqueId');
  const columnNameFromParam = searchParams.get('columnName');
  const selectedColumnUniqueIdFromParam = searchParams.get('selectedColumn');

  const nodeUniqueId = uniqueIdFromContext?.raw || uniqueId || uniqueIdFromParam;

  const [columnLineageQuery] = useColumnLineage({
    environmentId: environmentId,
    nodeUniqueId,
    columnName: columnName || columnNameFromParam,
  });

  const [relationLineage] = useColumnLineage({
    environmentId,
    nodeUniqueId,
    columnName: '*',
  });

  const consumerProjectsQuery = useConsumerProjects({ environmentId });

  const consumerProjectsMap = useMemo(() => {
    return (
      consumerProjectsQuery.data?.consumerProjects?.reduce<{
        [key: number]: any;
      }>((acc, obj) => {
        if (!obj.consumerProjectId) return acc;
        acc[obj.consumerProjectId] = obj;
        return acc;
      }, {}) || {}
    );
  }, [consumerProjectsQuery]);

  const producerProjectsQuery = useProducerProjects({ environmentId });
  const producerEnvironmentIds = useMemo(
    () =>
      producerProjectsQuery.data?.producerProjects
        ?.map((project) => project.producerEnvironmentId)
        .filter(truthy),
    [producerProjectsQuery.data?.producerProjects],
  );

  const modelUniqueIds = useMemo(
    () =>
      columnLineageQuery.data?.column.lineage
        ?.filter((n) => n.nodeUniqueId.startsWith('model.'))
        .map((n) => n.nodeUniqueId),
    [columnLineageQuery.data?.column.lineage],
  );

  const publicModels = useAllPublicModels({
    accountId,
    environmentIds: producerEnvironmentIds,
    uniqueIds: modelUniqueIds,
    pause:
      !producerEnvironmentIds?.length ||
      !modelUniqueIds?.length ||
      !uniqueId ||
      !columnName,
  });

  const isFetching =
    columnLineageQuery.fetching ||
    relationLineage.fetching ||
    publicModels.fetching ||
    consumerProjectsQuery.fetching ||
    producerProjectsQuery.fetching;

  const publicModelsData = publicModels.data;
  const publicNodesByUniqueId = useMemo(() => {
    if (publicModelsData) {
      return publicModelsData.reduce(
        (acc, model) => {
          acc[model.uniqueId] = model;
          return acc;
        },
        {} as Record<string, (typeof publicModelsData)[0]>,
      );
    }
    return {};
  }, [publicModelsData]);

  const { errorNodes, nodes } = useMemo(() => {
    const buildDAG = (
      columnLineageData: ColumnLineageNode[],
      errorData: ColumnLineageNode[],
    ) => {
      const errorNodes: ColumnLineageNode[] = [];
      const projectToParentColumnsMap: Record<number, string[]> = {};
      const projectIdToEnvironmentIdMap: Record<number, number> = {};
      const nodes: CLLNode[] = columnLineageData.map(
        (column: ColumnLineageNode): CLLNode => {
          if (column.dependentProjects) {
            column.dependentProjects.forEach((project) => {
              // This does mean CLL is assuming prod environments as edges
              projectIdToEnvironmentIdMap[project.projectId] =
                project.defaultEnvironmentId;
              projectToParentColumnsMap[project.projectId] ||= [];
              projectToParentColumnsMap[project.projectId].push(column.uniqueId);
            });
          }

          // if the column is from public column, we need to find the public model to get the correct project
          const publicNode = publicNodesByUniqueId[column.nodeUniqueId];

          let columnProjectInfo = {
            dbtCloudProject: getProjectName(column.projectId) || '',
            projectId: column.projectId || 0,
            environmentId: column.environmentId || 0,
          };
          if (publicNode) {
            columnProjectInfo = {
              dbtCloudProject: getProjectName(publicNode?.projectId!) || '',
              projectId: publicNode?.projectId || 0,
              environmentId: publicNode?.environmentId || 0,
            };
          }

          if (column.isError) {
            errorNodes.push(column);
            const parents = columnLineageData
              .filter((c: ColumnLineageNode) =>
                c.childColumns?.includes(column.uniqueId),
              )
              .map((c: ColumnLineageNode) => c.uniqueId);
            return {
              id: column.uniqueId,
              parents: [...(column.parentColumns ?? []), ...parents],
              label: getHumanReadableErrorType(column.errorCategory),
              labelIcon: column.isPrimaryKey ? 'primaryKey' : undefined,
              resourceType: 'error',
              access: column.dependentProjects?.length ? 'public' : undefined,
              transformationType: column.transformationType,
              isExternalNode: columnProjectInfo.projectId !== currentProjectId,
              data: {
                nodeUniqueId: column.nodeUniqueId,
                columnName: column.name,
                error: column.error ?? undefined,
                errorCategory: column.errorCategory ?? undefined,
              },
              ...columnProjectInfo,
            };
          }

          const { resourceType, remainder } = parseUniqueId(column.nodeUniqueId);
          return {
            id: column.uniqueId,
            parents: column.parentColumns ?? [],
            label: column.name,
            labelIcon: column.isPrimaryKey ? 'primaryKey' : undefined,
            sublabel: remainder,
            resourceType,
            isExternalNode: columnProjectInfo.projectId !== currentProjectId,
            access: column.dependentProjects?.length ? 'public' : undefined,
            transformationType: column.transformationType,
            data: {
              nodeUniqueId: column.nodeUniqueId,
              columnName: column.name,
            },
            ...columnProjectInfo,
          };
        },
      );

      // Return solo error node if no other CLL nodes available
      if (nodes.length === 0 && errorData.length > 0) {
        errorNodes.push(errorData[0]);
        nodes.push({
          id: errorData[0].uniqueId,
          parents: [],
          label: getHumanReadableErrorType(errorData[0].errorCategory),
          labelIcon: undefined,
          resourceType: 'error',
          access: undefined,
          transformationType: errorData[0].transformationType,
          data: {
            nodeUniqueId: errorData[0].nodeUniqueId,
            columnName: errorData[0].name,
            error: errorData[0].error ?? undefined,
            errorCategory: errorData[0].errorCategory ?? undefined,
          },
          dbtCloudProject: getProjectName(errorData[0].projectId) || '',
          projectId: errorData[0].projectId || 0,
          environmentId: errorData[0].environmentId || 0,
        });
      }

      const projectNodes = Object.entries(projectToParentColumnsMap)
        // don't show the current project as a "downstream project"
        .filter(([projectId]) => projectId !== currentProjectId?.toString())
        .map(([projectId, parentColumns]) => {
          const projectIdNum = parseInt(projectId);
          const projectName = getProjectName(projectIdNum) || '';
          return {
            id: projectId,
            resourceType: 'project',
            dbtCloudProject: projectName,
            projectId: projectIdNum,
            environmentId:
              consumerProjectsMap[parseInt(projectId)]?.consumerEnvironmentId!,
            annotation: 'Used in',
            parents: parentColumns,
            label: projectName,
            isExternalNode: projectIdNum !== currentProjectId,
          } satisfies CLLNode;
        });

      nodes.push(...projectNodes);

      return { errorNodes, nodes };
    };

    return buildDAG(
      columnLineageQuery.data?.column.lineage || [],
      relationLineage.data?.column.lineage || [],
    );
  }, [
    columnLineageQuery.data?.column.lineage,
    relationLineage.data?.column.lineage,
    consumerProjectsMap,
    currentProjectId,
    getProjectName,
    publicNodesByUniqueId,
  ]);

  const selectedNode = useMemo(() => {
    return nodes.find((n) => n.id === selectedColumnUniqueIdFromParam);
  }, [nodes, selectedColumnUniqueIdFromParam]);

  const sidePanelDetails = projectIdFromParam
    ? 'project'
    : selectedNode?.resourceType === 'error'
      ? 'error'
      : selectedNode
        ? 'column'
        : 'none';

  const queriedNode = columnLineageQuery.data?.column.lineage?.find(
    (n) => n.nodeUniqueId === uniqueId && n.name === columnName,
  );

  const { setLineageIngressPath } = useCurrentPage();
  const location = useLocation();

  const toolbarItems = useMemo((): ToolbarItem[] => {
    return [
      {
        ryecon: RyeconExpand,
        label: 'Full Lineage',
        tooltip: 'Enter Full Screen',
        action: () => {
          onOpenFullScreenLineage?.();
          navigate(
            getLineageLink({
              accountId: accountId || 0,
              projectId: currentProjectId || 0,
              dbtUniqueId: uniqueId || '',
              columnName: columnName || '',
              environmentType: getTargetEnvironmentType(environmentId),
            }),
          );
        },
        testId: `${columnName}-fullscreen-lineage`,
      },
    ];
  }, [
    columnName,
    onOpenFullScreenLineage,
    navigate,
    getLineageLink,
    accountId,
    currentProjectId,
    uniqueId,
    getTargetEnvironmentType,
    environmentId,
  ]);

  const status: QueryStatus =
    columnLineageQuery.error || relationLineage.error
      ? 'error'
      : columnLineageQuery.data && relationLineage.data
        ? 'success'
        : 'pending';

  const errorMessage =
    columnLineageQuery.error?.message || relationLineage.error?.message;

  return (
    <div className="relative flex h-full w-full">
      <span className="relative h-full w-full flex-1">
        <ExplorerDag
          testId={`cll-dag-${columnName}`}
          nodeType="lens"
          grain="column"
          nodes={nodes}
          status={status}
          showLoadingScreen={isFetching}
          errorMessages={errorMessage ? [errorMessage] : undefined}
          activeDbtCloudProject={projectName}
          zoomMode={isFullScreen ? 'fitAll' : '1+primary+1'}
          padding={isFullScreen ? undefined : 0.5}
          onNodeInteraction={({ targetNode, interactionType }) => {
            const isProject = targetNode?.resourceType === 'project';
            if (
              isFullScreen &&
              (interactionType === 'single_click' || interactionType === 'right_click')
            ) {
              setSearchParams((params) => {
                if (isProject) {
                  params.set('projectId', targetNode.projectId.toString());
                } else {
                  params.delete('projectId');
                  searchParams.set('selectedColumn', targetNode.id);
                }
                if (targetNode.environmentId) {
                  params.set('environmentId', targetNode.environmentId.toString());
                }
                return params;
              });
            } else if (interactionType === 'double_click') {
              if (targetNode.resourceType === 'project') {
                if (!targetNode.projectId || !targetNode.environmentId) {
                  return;
                }

                navigate(
                  getProjectLink({
                    accountId,
                    projectId: targetNode.projectId,
                    environmentType: getTargetEnvironmentType(
                      targetNode.environmentId!,
                    ),
                  }),
                );
              }
              const targetNodeUniqueId = targetNode?.data?.nodeUniqueId;
              const targetNodeError = targetNode?.data?.error;
              const targetColumnName = targetNode?.label;

              if (!targetNodeUniqueId || targetNodeError || !targetColumnName) return;
              let targetProjectScope = projectScope;
              if (currentProjectId !== targetNode.projectId) {
                targetProjectScope = {
                  accountId,
                  projectId: targetNode.projectId,
                  environmentType: getTargetEnvironmentType(targetNode.environmentId),
                };
              }
              const targetUrl = getDetailsUrl({
                ...targetProjectScope,
                dbtUniqueId: targetNodeUniqueId,
                tab: 'columns',
                originColumn: targetColumnName,
              });

              navigate(targetUrl);
            } else if (interactionType === 'single_click') {
              if (targetNode.resourceType === 'error' && !isFullScreen) {
                if (columnName) {
                  searchParams.set('originColumn', columnName);
                }
                setLineageIngressPath?.(
                  `${location.pathname}?${searchParams.toString()}`,
                );
                navigate(
                  getLineageLink({
                    accountId: accountId || 0,
                    projectId: currentProjectId || 0,
                    dbtUniqueId: uniqueId || '',
                    columnName: columnName || '',
                    selectedColumn: targetNode.id,
                    environmentType: getTargetEnvironmentType(environmentId),
                  }),
                );
              } else {
                setSearchParams((searchParams) => {
                  searchParams.set('selectedColumn', targetNode.id);
                  return searchParams;
                });
              }
            } else if (interactionType === 'clear_selection') {
              setSearchParams((params) => {
                params.delete('selectedColumn');
                params.delete('environmentId');
                return params;
              });
            }
          }}
          primaryNodeIds={
            isFullScreen
              ? [selectedNode?.id || queriedNode?.uniqueId || '']
              : [queriedNode?.uniqueId || '']
          }
          toolbarItems={!isFullScreen ? toolbarItems : []}
          showResetButton={!isFullScreen}
        >
          <Dag.ZoomControls />
          {hasAccountFeature('explorerLineageLenses') && (
            <Dag.LensSwitcher lenses={['transformation']} />
          )}
          {hasAccountFeature('explorerLineageLenses') && (
            <Dag.LensLegend initialLens={'transformation'} />
          )}
        </ExplorerDag>
        <div className="absolute left-0 right-0 top-0">
          {isFullScreen && (
            <LineageFloatingNavBar
              granularity="column"
              view="lineage"
              accountName={accountName}
              projectName={projectName}
              accountId={accountId}
              projectId={currentProjectId}
            />
          )}
        </div>

        {!!errorNodes.length && (
          <div
            className={twMerge(
              'pointer-events-none absolute left-6 right-6 top-6',
              isFullScreen && 'top-5',
            )}
          >
            <div
              className={twJoin(
                'pointer-events-auto relative flex w-fit items-center gap-1 rounded-md bg-bgDisabled px-2 py-3 text-fgMain',
                isFullScreen && 'mx-auto',
              )}
            >
              <span>Column lineage may be missing columns</span>
              <Link
                className="inline-flex items-center"
                to="https://docs.getdbt.com/docs/collaborate/column-level-lineage"
                isInternal={false}
                shouldOpenNewTab
              >
                <IconButton
                  tooltip="Check the Column-Level Lineage docs"
                  ryecon={RyeconInfoOutline}
                  size={Sizes.sm}
                />
              </Link>
            </div>
          </div>
        )}
      </span>
      {isFullScreen && (
        <span className="flex-0 text-left">
          <LineagePanelView
            isOpen={sidePanelDetails !== 'none'}
            propsToFreeze={{
              projectIdFromParam,
              environmentIdFromParam,
              sidePanelDetails,
              selectedNode,
            }}
          >
            {({
              projectIdFromParam,
              environmentIdFromParam,
              sidePanelDetails,
              selectedNode,
            }) => (
              <>
                {sidePanelDetails === 'error' && (
                  <ColumnErrorPanelContents
                    accountId={accountId}
                    projectId={currentProjectId}
                    modelUniqueId={selectedNode?.data?.nodeUniqueId ?? ''}
                    error={selectedNode?.data?.error}
                    errorCategory={selectedNode?.data?.errorCategory}
                  />
                )}
                {sidePanelDetails === 'column' && (
                  <ColumnPanelContents
                    nodeUniqueId={selectedNode?.data?.nodeUniqueId}
                    column={selectedNode?.data?.columnName}
                    environmentId={environmentIdFromParam}
                  />
                )}
                {sidePanelDetails === 'project' && (
                  <ProjectPanelContents
                    projectId={projectIdFromParam}
                    environmentId={environmentIdFromParam}
                  />
                )}
              </>
            )}
          </LineagePanelView>
        </span>
      )}
    </div>
  );
};
